import { toast } from 'sonner';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useAssessmentReport } from './hooks/useTakeAssessment';
import { downloadReportPdf } from './api/reportApi';
import { isV2Report, updateActionStatus, type V2ActionStatus } from './api/v2ReportApi';
import { ReportView } from './components/ReportView';
import { V2ReportView } from './components/V2ReportView';

/**
 * Authed results report page (Feature 095, Slice 3). Reachable by the client
 * (their own) and the advisor (when shared). The report endpoint gates access —
 * a 404 renders the "not available" state.
 *
 * Feature 110 added a second report version behind the same route and the same
 * link: the page renders whichever shape came back rather than deciding for
 * itself, so an assessment sent before MVP 2 still opens as the V1 report.
 */
export function AssessmentResultsPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { data: report, isLoading, isError } = useAssessmentReport(id);
  const [downloading, setDownloading] = useState(false);

  // Refetch rather than patch the cache: the status is stored server-side and
  // the rest of the report is derived, so the round trip is the honest read.
  const setStatus = useMutation({
    mutationFn: ({ needId, status }: { needId: string; status: V2ActionStatus }) =>
      updateActionStatus(id, needId, status),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['longevity-assessment', 'report', id] }),
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Fetches the freshly-rendered PDF as an authed blob and triggers the
      // browser download (object URL + anchor) — no new tab needed.
      await downloadReportPdf(id);
    } catch (error) {
      // Non-fatal — the web report stays available — but say so, rather than
      // returning the button to its resting state with nothing to show.
      console.error('Failed to download the report PDF:', error);
      toast.error('Could not prepare the PDF. Your results on this page are unaffected.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
          {/* Both versions now: the route renders V1's template or MVP 2's from
              the assessment's stored question set, so one button serves both. */}
          {report && (
            <Button variant="outline" size="sm" className="gap-2" disabled={downloading} onClick={handleDownload}>
              <Download className="h-4 w-4" />
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          )}
        </div>

        {isLoading && <p className="text-center text-gray-500">Loading your report…</p>}
        {(isError || (!isLoading && !report)) && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
            <h2 className="font-serif text-2xl text-gray-900">Report not available</h2>
            <p className="mx-auto mt-3 max-w-md text-[15px] text-gray-600">
              This report doesn&apos;t exist or isn&apos;t shared with you.
            </p>
          </div>
        )}
        {report &&
          (isV2Report(report) ? (
            <V2ReportView
              report={report}
              onStatusChange={(needId, status) => setStatus.mutate({ needId, status })}
              savingNeedId={setStatus.isPending ? setStatus.variables?.needId : null}
            />
          ) : (
            <ReportView report={report} />
          ))}
      </div>
    </div>
  );
}
